import React, { useEffect, useState } from "react";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getUserValidationSchema } from "utils/validations";
import { getRolesList } from "store/slices/rolesSlice";
import {
  formatOptionsName,
  formatOptionsNameWithLangObject,
} from "utils/formatOptions";
import { handleSubmitForm } from "helpers/apiUtils";
import { addUser, getUserDetails, updateUser } from "store/slices/usersSlice";
import { useNavigate } from "react-router-dom";
import FormShimmer from "components/common/shimmer/FormShimmer ";
import { usePermission } from "hooks/usePermission";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import { ADMIN_ACCOUNT_TYPE } from "utils/constants";
import MultiSelect from "components/common/MultiSelect";
import { getBranchesList } from "store/slices/branchesSlice";

const COUNTRY_CODE_OPTIONS = [{ name: "+966", value: "+966" }];

const AddEditUser = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isEdit = mode === "edit";
  const id = useQueryParam("id");
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("users");

  const {
    itemDetails: userDetails,
    loading,
    isLoading,
  } = useSelector((state) => state.Users);

  const { itemsList: rolesList } = useSelector((state) => state.Roles);
  const { itemsList: branchesList } = useSelector((state) => state.Branches);

  const [initialValues, setInitialValues] = useState({
    accountType: "admin",
    name: "",
    email: "",
    phoneCode: "+966",
    phoneNumber: "",
    roleId: "",
    branch: "", // ← add this
    branches: [],
  });

  useEffect(() => {
    dispatch(getRolesList());
    dispatch(getBranchesList());

    if (isEdit && id) {
      dispatch(getUserDetails(id));
    }
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && userDetails) {
      const {
        email,
        name,
        phoneCode,
        phoneNumber,
        roleId,
        accountType,
        branch,
        branches,
      } = userDetails || {};
      setInitialValues({
        ...initialValues,
        name,
        email,
        phoneCode: phoneCode || "+966",
        phoneNumber,
        roleId: roleId?._id,
        accountType,
        branch: branches?.[0]?._id ?? branch ?? "", // ← add
        branches: branches?.map((b) => b?._id ?? b) ?? [], // ← add
      });
    }
  }, [isEdit, userDetails]);

  const handleSubmit = (values) => {
    const { branch, branches, phoneCode, phoneNumber, roleId, ...rest } =
      values;
    const isBranchUser = ["staff", "pos"].includes(values.accountType);

    const payload = {
      ...rest,
      ...(roleId && { roleId }),
      ...(values.phoneNumber && { phoneCode, phoneNumber }),
      ...(values.accountType === "admin" && { branches }),
      ...(isBranchUser && branch && { branch }),
    };
    if (isEdit) {
      handleSubmitForm({
        dispatch,
        action: updateUser,
        payload: { id, ...payload },
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addUser,
        payload: payload,
        onSuccess: () => navigate(-1),
      });
    }
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return <AddEditLoader title={t("Edit User")} rows={2} cols={3} />;

  return (
    <div className="page-content add_user_page">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit User") : t("Add User")}
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getUserValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                {/* ACCOUNT TYPE */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>
                      {t("Account Type")}
                      <span className="text-danger">*</span>
                    </label>
                    <SingleSelect
                      value={values.accountType}
                      options={ADMIN_ACCOUNT_TYPE.map((option) => ({
                        ...option,
                        name: t(option.name),
                      }))}
                      handleSingleSelectChange={(e) => {
                        setFieldValue("accountType", e.value);
                      }}
                      error={
                        errors.accountType && touched.accountType
                          ? errors.accountType
                          : null
                      }
                      placeholder={t("Select option")}
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name")}
                      name="name"
                      placeholder={t("Enter name")}
                      required
                    />
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Email")}
                      name="email"
                      placeholder={t("Enter email")}
                      isReadOnly={isEdit}
                      required
                    />
                  </div>
                </Col>

                <Col lg={1}>
                  <div className="form_field">
                    <label>
                      {t("Country Code")}
                      <span className="text-danger"></span>
                    </label>
                    <SingleSelect
                      value={values.phoneCode}
                      options={COUNTRY_CODE_OPTIONS}
                      handleSingleSelectChange={(e) =>
                        setFieldValue("phoneCode", e.value)
                      }
                      error={
                        errors.phoneCode && touched.phoneCode
                          ? errors.phoneCode
                          : null
                      }
                      placeholder={t("Select option")}
                    />
                  </div>
                </Col>

                <Col lg={3}>
                  <div className="form_field">
                    <CustomField
                      label={t("Phone Number")}
                      name="phoneNumber"
                      placeholder="5XXXXXXXX"
                      required={values.accountType === "pos"}
                    />
                  </div>
                </Col>

                {/* Role */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>
                      {t("Role")}
                      {values.accountType === "admin" && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <SingleSelect
                      value={values.roleId}
                      options={formatOptionsName(
                        rolesList.filter((value) => {
                          return value.roleType === values.accountType;
                        }),
                        language,
                      )}
                      handleSingleSelectChange={(e) =>
                        setFieldValue("roleId", e.value)
                      }
                      error={
                        errors.roleId && touched.roleId ? errors.roleId : null
                      }
                      placeholder={t("Select option")}
                    />
                  </div>
                </Col>

                {values.accountType === "admin" && (
                  <Col lg={4}>
                    <div className="form_field">
                      <MultiSelect
                        label={t("Branches")}
                        name="branches"
                        value={values.branches}
                        options={formatOptionsNameWithLangObject(
                          branchesList,
                          language,
                        )}
                        placeholder={t("Select Branches")}
                        onChange={(val) => setFieldValue("branches", val)}
                      />
                      {errors.branches && touched.branches && (
                        <div className="text-danger">{errors.branches}</div>
                      )}
                    </div>
                  </Col>
                )}

                {["staff", "pos"].includes(values.accountType) && (
                  <Col lg={4}>
                    <div className="form_field">
                      <label>{t("Branch")}</label>
                      <SingleSelect
                        value={values.branch}
                        options={formatOptionsNameWithLangObject(
                          branchesList,
                          language,
                        )}
                        handleSingleSelectChange={(e) =>
                          setFieldValue("branch", e.value)
                        }
                        error={
                          errors.branch && touched.branch ? errors.branch : null
                        }
                        placeholder={t("Select option")}
                      />
                    </div>
                  </Col>
                )}

                <Col lg={12} className="mt-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                    >
                      {t("Cancel")}
                    </button>

                    <button
                      type="submit"
                      className="main_btn"
                      disabled={isLoading}
                    >
                      {isEdit ? t("Save Changes") : t("Create User")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>{" "}
    </div>
  );
};

export default AddEditUser;
